import React, { useState, useEffect } from 'react';
import { Drawer, Modal, Button, Checkbox, Spin } from 'antd';
import PropTypes from 'prop-types';
import { CloseIcon } from '../../assets/icons';
import useGetUserProfile from '../../Hooks/useGetUserProfile';
import useMediaQuery from '../../Hooks/useMediaQuery';
import { getSecondaryMarketAgreementApi, acceptSecondaryListingAgreementApi } from '../../services/authApi/userApi';
import { toastNotify } from '../../utils/toastNotify';
import './SecondaryListingAgreementModal.scss';

const SecondaryListingAgreementModal = ({ visible, onClose, onSuccess }) => {
  const { userProfileData } = useGetUserProfile();
  const screenMD = useMediaQuery('(min-width: 1000px)');
  const [checkbox1, setCheckbox1] = useState(false);
  const [checkbox2, setCheckbox2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreementData, setAgreementData] = useState('');
  const [agreementTitle, setAgreementTitle] = useState('Secondary Listing Agreement');
  const [fetchingAgreement, setFetchingAgreement] = useState(false);

  const legalName = `${userProfileData?.firstName || ''} ${userProfileData?.lastName || ''}`.trim();

  // Fetch agreement from backend
  useEffect(() => {
    const fetchAgreement = async () => {
      if (visible) {
        setFetchingAgreement(true);
        try {
          const resData = await getSecondaryMarketAgreementApi();
          
          if (resData?.isError) {
            toastNotify('error', resData?.message || 'Failed to load agreement');
          } else {
            // Parse the full HTML document to extract title, styles and body content
            const parser = new DOMParser();
            const doc = parser.parseFromString(resData, 'text/html');
            const title = doc.querySelector('title')?.textContent?.trim();
            const styleContent = Array.from(doc.querySelectorAll('head style')).map(s => s.outerHTML).join('');
            const bodyContent = doc.body?.innerHTML || '';
            if (title) setAgreementTitle(title);
            setAgreementData(styleContent + bodyContent);
          }
        } catch (error) {
          const errorMessage = error?.response?.data?.message || 'Failed to load agreement';
          toastNotify('error', errorMessage);
        } finally {
          setFetchingAgreement(false);
        }
      }
    };

    fetchAgreement();
  }, [visible]);

  const handleAgreeAndContinue = async () => {
    setLoading(true);
    try {
      const resData = await acceptSecondaryListingAgreementApi();
      if (resData?.isError) {
        toastNotify('error', resData?.message || 'Failed to accept agreement');
      } else {
        toastNotify('success', resData?.message || 'Agreement accepted successfully');
        onSuccess();
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to accept agreement';
      toastNotify('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCheckbox1(false);
    setCheckbox2(false);
    onClose();
  };

  const isButtonDisabled = !checkbox1 || !checkbox2 || loading || fetchingAgreement;

  const renderContent = () => (
    <Spin spinning={loading || fetchingAgreement}>
      <div className='agreement-content fz-14'>
        {agreementData ? (
          <div dangerouslySetInnerHTML={{ __html: agreementData }} />
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
            {fetchingAgreement ? 'Loading agreement...' : 'No agreement content available'}
          </div>
        )}
        <div className='checks mb-24'>
          <Checkbox
            className='ml-8'
            checked={checkbox1}
            onChange={(e) => setCheckbox1(e.target.checked)}
            disabled={loading || fetchingAgreement}
          >
            {`I attest that I am ${legalName || '[Your Legal Name]'} and I understand that the signature herein is a legal representation of my own.`}
          </Checkbox>

          <Checkbox
            disabled={loading || fetchingAgreement}
            checked={checkbox2}
            onChange={(e) => setCheckbox2(e.target.checked)}
            className='ml-0'
          >
            I have read and agree to the terms of the Secondary Listing Agreement.
          </Checkbox>
        </div>
      </div>
    </Spin>
  );

  const renderFooter = () => (
    <Button
      loading={loading}
      type='primary'
      className='w-100'
      disabled={isButtonDisabled}
      onClick={handleAgreeAndContinue}
    >
      Agree to terms
    </Button>
  );

  if (screenMD) {
    return (
      <Modal
        className='quickbuy agreement-modal ant-modal--footer-shadow ant-modal--footer'
        open={visible}
        width={920}
        destroyOnClose={true}
        closeIcon={<CloseIcon onClick={handleClose} />}
        footer={renderFooter()}
        centered
      >
        {renderContent()}
      </Modal>
    );
  }

  return (
    <Drawer
      className='agreement footer-shadow quickbuy--drawer'
      width='100%'
      open={visible}
      title={<span className='subhead-2 mb-0 ml-40 text-center'>{agreementTitle}</span>}
      onClose={handleClose}
      closeIcon={<CloseIcon disabled={loading} onClick={handleClose} />}
      footer={renderFooter()}
      centered
    >
      {renderContent()}
    </Drawer>
  );
};

SecondaryListingAgreementModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default SecondaryListingAgreementModal;
